/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Intent, Loader, Shape, Size } from '@axieinfinity/matcha'
import { EyeIcon, EyeSlashIcon } from '@axieinfinity/matcha-icons'
import clsx from 'clsx'
import { useCallback, useEffect, useState } from 'react'
import { QRCode as ReactQRCode } from 'react-qrcode-logo'

import { refreshTokenFn } from '~/apis/request'
import { Box } from '~/components/Box'
import { getAccessToken } from '~/core/cookie'
import { useSentry } from '~/core/sentry'
import { useCountDown } from '~/hooks/useCountDown'
import RoninLogo from '~/public/images/dark-ronin-logo.png'
import { diffTimeToNow, toStringTime } from '~/utils/helper'

import styles from './styles.module.scss'

const NAME_QR_DOWNLOAD = 'qrcode-sign-in-game.png'

type TTokenQR = {
  value: string
  expires: string
}

export const QRCode: React.FC = () => {
  const [hide, setHide] = useState(true)
  const [token, setToken] = useState<TTokenQR>({
    value: '',
    expires: '',
  })

  const { captureException } = useSentry()

  const diff = diffTimeToNow(token.expires)

  const { counter, restart, start } = useCountDown(diff)

  const handleRefreshToken = useCallback(async () => {
    try {
      const data = await refreshTokenFn()

      if (data) {
        setToken({
          value: data?.accessToken || '',
          expires: data?.accessTokenExpiresAt || '',
        })

        const diff = diffTimeToNow(data?.accessTokenExpiresAt || '')

        restart(diff)
      }
    } catch (error) {
      captureException(error)
    }
  }, [captureException])

  useEffect(() => {
    const { accessToken: value, expireAt: expires } = getAccessToken()

    setToken({
      value,
      expires,
    })
    start()
  }, [])

  useEffect(() => {
    if (counter === 0 && token.expires) {
      handleRefreshToken()
    }
  }, [counter])

  const handleDownloadQR = () => {
    const canvas = document.getElementById(
      'qrcode-dashboard',
    ) as HTMLCanvasElement
    const link = document.createElement('a')
    link.download = NAME_QR_DOWNLOAD
    link.href = canvas.toDataURL()
    link.click()
    link.remove()
  }

  const IconHideQRCode = hide ? EyeIcon : EyeSlashIcon

  return (
    <Box className="flex flex-col md:flex-row md:justify-between gap-24 p-24">
      <div className="w-full md:w-1/2">
        <div className="text-h6 mb-4">QR Code</div>
        <div className="text-body-s text-text-color-subdued">
          You can sign in to supporting games by scanning or uploading this QR
          code. Please store this code securely.
        </div>
      </div>
      <div className="flex-1">
        <div className="border border-border-color-default-subdued flex flex-col items-center bg-surface-color-default-subdued pt-24 pb-16 rounded-12">
          <div className="relative h-[187px] w-[187px] overflow-hidden rounded-12 border border-border-color-default">
            {token.value ? (
              <>
                <div
                  className={clsx(
                    'absolute inset-0 flex items-center justify-center',
                    {
                      // @ts-ignore
                      [styles['qr-overlay']]: hide,
                    },
                  )}
                />

                <ReactQRCode
                  id="qrcode-dashboard"
                  value={token?.value || ''}
                  enableCORS
                  size={164}
                  qrStyle="dots"
                  ecLevel="M"
                  bgColor="#e6e7e7"
                  fgColor="#0E1116"
                  logoOpacity={100}
                  logoPaddingStyle="square"
                  logoImage={RoninLogo}
                  eyeRadius={[
                    { outer: 4, inner: 1 },
                    { outer: 4, inner: 1 },
                    { outer: 4, inner: 1 },
                  ]}
                  logoHeight={32}
                  logoWidth={23}
                />
              </>
            ) : (
              <div className="flex items-center justify-center h-[187px] w-[187px]">
                <Loader.Circle size={Size.Small} intent={Intent.Primary} />
              </div>
            )}
          </div>

          <div className="text-body-s text-text-color-subdued mt-16">
            QR code will be refreshed in {toStringTime(counter)}
          </div>
        </div>

        <div className="flex gap-8 mt-12">
          <Button
            fullWidth
            text={hide ? 'Reveal QR code' : 'Hide QR code'}
            icon={IconHideQRCode}
            onClick={() => setHide(pre => !pre)}
            shape={Shape.Default}
            intent={Intent.Default}
          />

          <Button
            fullWidth
            text="Download"
            onClick={handleDownloadQR}
            shape={Shape.Default}
            intent={Intent.Default}
          />
        </div>
      </div>
    </Box>
  )
}
